import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import * as url from 'url';

import { AssignPatientDto } from '../dto/AssignPatientDto';
import { postProcessAxiosError, prepareRequestAndFillFormData } from '@shared/api/base-api';
import { DevicePageRequestParams, UpdateOutOfUseRequestParams } from '@shared/api/core-types';
import { DeviceAssignmentInfoDto, DeviceAssignmentItemDto } from '@shared/api/dto/DeviceAssignmentDto';
import { DeviceDataSource } from '@shared/api/dto/DeviceDataSource';
import { DeviceGeneration } from '@shared/api/dto/DeviceGeneration';
import { DeviceOutgoingStates } from '@shared/api/dto/DeviceOutgoingStates';
import { UpdateOutOfUseDto } from '@shared/api/dto/DeviceUpdateOutOfUseDto';
import { MoveDeviceDto } from '@shared/api/dto/MoveDeviceDto';
import { Page } from '@shared/api/dto/Page';
import { PATH_TO_CORE_API } from '@shared/constants';
import { SortDirections } from '@shared/constants/SortDirections';
import { PreregisteredDevicesUploadData } from '@shared/types/devicePreRegistrationTypes';

export const ASSIGNED_DEVICES_PAGE_SIZE = 15;

const defaultOpts: AxiosRequestConfig = {
  baseURL: PATH_TO_CORE_API,
};

export interface AssignedPatientDto {
  lastName: string;
  firstName: string;
  prismaCloudId: string;
}

export interface ModemInfo {
  iccid: string;
  imei: string;
}

export enum P5SendDataTriggerStatus {
  /*
   * Send data trigger is not supported, because of invalid generation.
   */
  NotSupportedByGeneration = 'NotSupportedByGeneration',
  /*
   * Send data trigger is not supported because device is not online.
   */
  NotOnline = 'NotOnline',
  /*
   * Send data trigger supported.
   */
  Supported = 'Supported',
  /*
   * Send all data was triggered.
   */
  TriggeredSendAll = 'TriggeredSendAll',
  /*
   * Send current data was triggered.
   */
  TriggeredSendCurrent = 'TriggeredSendCurrent',
}

export interface DataTriggerInfo {
  lastSendDataTriggeredAt: string;
  sendDataTrigger: P5SendDataTriggerStatus;
}

export interface DeviceDto {
  serialNumber: string;
  generation: DeviceGeneration;
  brandName: string;
  currentBindingId: string;
  firmwareVersion: string;
  /*
   * The latest date when data was received for device via LastDeviceDataSource.
   * Null in case if LastDeviceDataSource is NoData or PreRegistered.
   */
  lastDataReceivedAt: string | null;
  lastDeviceDataSource: DeviceDataSource;
  modemInfo: ModemInfo | null;
  assignedPatient: AssignedPatientDto | null;
  isDeviceTimeWrong: boolean;
  isMissingModemTransmission: boolean;
  isMissingData: boolean;
  missingDataThresholdInDays?: number;
  isOutOfUse: boolean;
  isTelemonitoringEnabled: boolean;
  supportsTelemonitoringSwitch: boolean;
  faultedTherapyConfiguration: {
    faultedAt: string;
    lastFaultedState: DeviceOutgoingStates;
  } | null;
  hasAnyPatientAppUser: boolean;
  lastSendDataTriggeredAt: string;
  sendDataTrigger: P5SendDataTriggerStatus;
}

export type AssignPatientSortField = 'additionalId' | 'name' | 'dateOfBirth' | 'createdAt';

export interface DevicePageDto extends Page<DeviceDto> {
  unfilteredTotalDeviceCount: number;
}

/* Summary of moved devices to partner organisation via UI. */
export interface DevicesMovedSummary {
  /* Created batch ID */
  id: string;
  /* Amount of devices, which have been moved */
  deviceCount: number;
  /* Tenant to which the devices have been moved */
  targetTenant: string;
  /* E-Mail of the user, who moved the device */
  movedByMail: string;
  /* Time of movement */
  movedAt: string;
}

export interface DeviceValidationResultForMovement {
  serialNumber: string;
  status: 'NotFound' | 'AssignedToPatient' | 'NotAssignedToPatient';
  assignedPatientInfo: { firstName: string; lastName: string } | null;
}

export const DevicesApi = {
  async getDataTriggerInfo(serialNumber: string, options?: AxiosRequestConfig): Promise<DataTriggerInfo> {
    const response: AxiosResponse<DataTriggerInfo> = await axios.get(`/devices/data-trigger-info/${serialNumber}`, {
      ...defaultOpts,
      ...options,
    });
    return response.data;
  },
  async getDevicesForAssignment(
    args: {
      searchQuery: string | null;
      sortField: 'serialNumber' | 'lastDataReceivedAt' | 'assignedPatient' | 'assignedUser';
      sortDirection: SortDirections;
      includeDevicesWithPatient: boolean;
      pageSize: number;
      pageNumber: number;
    },
    options?: AxiosRequestConfig,
  ) {
    const response: AxiosResponse<Page<DeviceAssignmentItemDto>> = await axios.post('/devices/page-short-info', args, {
      ...defaultOpts,
      ...options,
    });

    return response.data;
  },
  async getPage(
    searchQuery: string | null,
    requestParams: DevicePageRequestParams,
    options?: AxiosRequestConfig,
  ): Promise<DevicePageDto> {
    const params = url.format({ query: requestParams as unknown as any });
    const response: AxiosResponse<DevicePageDto> = await axios.post(
      `/devices/page${params}`,
      { text: searchQuery },
      { ...defaultOpts, ...options },
    );

    return response.data;
  },
  async getAssignmentInfo(deviceId: number, options?: AxiosRequestConfig): Promise<DeviceAssignmentInfoDto> {
    const response = await axios.get(`/devices/assignment/${deviceId}`, { ...defaultOpts, ...options });
    return response.data;
  },
  async updateOutOfUse(params: UpdateOutOfUseRequestParams, options?: AxiosRequestConfig): Promise<UpdateOutOfUseDto> {
    const response = await axios.post('/devices/updateOutOfUse', params, { ...defaultOpts, ...options });
    return response.data;
  },
  async moveDevice(dto: MoveDeviceDto, options?: AxiosRequestConfig): Promise<void> {
    await axios.post('/deviceMovement', dto, { ...defaultOpts, ...options });
  },
  async assignPatient(dto: AssignPatientDto, options?: AxiosRequestConfig) {
    await axios.post('/devices/assign-patient', dto, { ...defaultOpts, ...options });
  },
  async uploadPreregisteredDevices(file: any, options?: AxiosRequestConfig): Promise<PreregisteredDevicesUploadData[]> {
    const localVarAxiosArgs = prepareRequestAndFillFormData(file, options, '/devices/pre-register');
    const baseURL = defaultOpts.baseURL;
    const axiosRequestArgs = {
      ...localVarAxiosArgs.options,
      url: baseURL + localVarAxiosArgs.url,
    };
    try {
      const result: AxiosResponse<PreregisteredDevicesUploadData[]> = await axios.request(axiosRequestArgs);
      return result.data;
    } catch (error) {
      return postProcessAxiosError(error);
    }
  },
  async movePreRegisteredDevices(file: any, options?: AxiosRequestConfig): Promise<void> {
    const localVarAxiosArgs = prepareRequestAndFillFormData(file, options, '/devices/move-pre-registered');
    const baseURL = defaultOpts.baseURL;
    const axiosRequestArgs = {
      ...localVarAxiosArgs.options,
      url: baseURL + localVarAxiosArgs.url,
    };
    try {
      await axios.request(axiosRequestArgs);
    } catch (error) {
      return postProcessAxiosError(error);
    }
  },
  async switchTelemonitoring(
    serialNumber: string,
    deviceGeneration: DeviceGeneration,
    isEnabled: boolean,
    options?: AxiosRequestConfig,
  ): Promise<void> {
    await axios.post(
      `/devices/switch-telemonitoring/${serialNumber}${url.format({ query: { deviceGeneration, isEnabled } })}`,
      {},
      { ...defaultOpts, ...options },
    );
  },
  async getAllDevicesAsCsv(options?: AxiosRequestConfig): Promise<ArrayBuffer> {
    const response: AxiosResponse<ArrayBuffer> = await axios.get('/devices/export', {
      ...defaultOpts,
      ...options,
    });

    return response.data;
  },
  async triggerSendAll(serialNumber: string, options?: AxiosRequestConfig): Promise<null> {
    await axios.post(`/devices/trigger-send-all/${serialNumber}`, {}, { ...defaultOpts, ...options });

    return null;
  },
  async triggerSendCurrent(serialNumber: string, options?: AxiosRequestConfig): Promise<null> {
    await axios.post(`/devices/trigger-send-current/${serialNumber}`, {}, { ...defaultOpts, ...options });

    return null;
  },
  async deactivateDevice(serialNumber: string, options?: AxiosRequestConfig): Promise<null> {
    await axios.delete(`/devices/deactivate/${serialNumber}`, { ...defaultOpts, ...options });
    return null;
  },
  async validateDevicesForMovement(
    file: File,
    options?: AxiosRequestConfig,
  ): Promise<DeviceValidationResultForMovement[] | undefined> {
    const localVarAxiosArgs = prepareRequestAndFillFormData(file, options, '/devicemovement/validate');
    const baseURL = defaultOpts.baseURL;
    const axiosRequestArgs = {
      ...localVarAxiosArgs.options,
      url: baseURL + localVarAxiosArgs.url,
    };
    const result: AxiosResponse<DeviceValidationResultForMovement[]> = await axios.request(axiosRequestArgs);
    return result.data;
  },
  async moveToAnotherOrganisationViaCsv(
    targetTenant: string,
    copyStatistics: boolean,
    file: File,
    options?: AxiosRequestConfig,
  ): Promise<DevicesMovedSummary> {
    const query = url.format({ query: { copyStatistics } });

    const localVarAxiosArgs = prepareRequestAndFillFormData(
      file,
      options,
      `/devicemovement/csv/${targetTenant}${query}`,
    );

    const baseURL = defaultOpts.baseURL;
    const axiosRequestArgs = {
      ...localVarAxiosArgs.options,
      url: baseURL + localVarAxiosArgs.url,
    };
    const result: AxiosResponse<DevicesMovedSummary> = await axios.request(axiosRequestArgs);
    return result.data;
  },
};
